import { arc, circle, segment, vec, type Shape } from '../geom';
import { DIMENSION_KINDS } from '../dimensions';
import { ARROW_TYPES, LINE_TYPES, ROUND_TEXTS, STYLE_KEYS } from '../model';

const LINE_NAMES = Object.keys(LINE_TYPES);

/** The file name scripts run under, so error positions can be told apart from the API's. */
export const SCRIPT_FILE = 'drawing.js';
export const API_FILE = 'api.js';
/** The host function the API calls for geometry; the API captures it and removes the name. */
export const GEOMETRY_FUNCTION = '__geometry';
/** Where the host puts the drawing's param values; the API captures them and removes the name. */
export const PARAMS_GLOBAL = '__params';
/** Where the host puts the drawing's layer names, layer 0 first; captured and removed likewise. */
export const LAYERS_GLOBAL = '__layers';
/** Set by the host when a run should report each shape's line; captured and removed likewise. */
export const LINES_GLOBAL = '__lines';
/** Where the host puts the drawing's view margin; captured and removed likewise. */
export const VIEW_MARGIN_GLOBAL = '__viewMargin';
/**
 * Where the host puts each layer's dimension sizes, layer 0 first, as `[textHeight,
 * arrowSize, arrow index]` with -1 where the layer sets none; captured and removed likewise.
 */
export const LOOKS_GLOBAL = '__looks';
/** Records name shapes by their index in `CALLS`; dimensions follow, in `DIMENSION_KINDS` order. */
const SHAPE_CODES = 3;
const DIMENSION_CODES = Object.fromEntries(
  DIMENSION_KINDS.map((kind, index) => [kind, SHAPE_CODES + index]),
);
/** A view's record code, after the dimensions'; it records its margin. */
export const VIEW_CODE = SHAPE_CODES + DIMENSION_KINDS.length;

/**
 * The drawing API, defined inside the sandbox as `Point`, `Line`, `Circle`, `Arc`,
 * `Dimension`, `View`, `params` and `layers`.
 *
 * - Shapes change in place when edited, and every shape that is not a guide is drawn as it
 *   stands when the script ends, recorded into one flat array of numbers handed back as one
 *   binary buffer.
 * - Geometry questions cross to the host's `core/geom` once per call, as JSON, so scripts
 *   and the canvas share one implementation; point arithmetic stays inside.
 * - Built-ins are captured before the script runs, so a script that replaces `JSON` or
 *   `Math` cannot change what is recorded or sent.
 *
 * The source is plain JavaScript in a template string, so it avoids backticks.
 */
export const API_SOURCE = `
(() => {
  const stringify = JSON.stringify;
  const parse = JSON.parse;
  const isArray = Array.isArray;
  const { cos, sin, atan2, hypot, abs, min, PI } = Math;
  const Float64 = Float64Array;
  const Err = Error;
  const host = globalThis.${GEOMETRY_FUNCTION};
  delete globalThis.${GEOMETRY_FUNCTION};
  // The drawing's params, set in the Parameters panel; the host sets them before this runs.
  const values = globalThis.${PARAMS_GLOBAL} || {};
  delete globalThis.${PARAMS_GLOBAL};
  // The drawing's layer names, layer 0 first, made in the Layers panel.
  const layerNames = globalThis.${LAYERS_GLOBAL} || ['0'];
  delete globalThis.${LAYERS_GLOBAL};
  // Only a run that asks for lines keeps each shape's stack, since making one roughly
  // doubles the cost of drawing a shape.
  const withLines = globalThis.${LINES_GLOBAL} === true;
  delete globalThis.${LINES_GLOBAL};
  // How far a view's frame stands clear of what it holds, set in the Document section.
  const viewMargin = globalThis.${VIEW_MARGIN_GLOBAL};
  delete globalThis.${VIEW_MARGIN_GLOBAL};
  const layerLooks = globalThis.${LOOKS_GLOBAL} || [];
  delete globalThis.${LOOKS_GLOBAL};

  const shapes = [];
  // Recorded with index writes, so a script that replaces Array methods cannot change them.
  const out = [];
  let n = 0;


  // x - x is 0 only for a finite number, which rules out NaN and the infinities in one test.
  const finite = (value) => typeof value === 'number' && value - value === 0;
  const number = (value, what) => {
    if (!finite(value)) throw new TypeError(what + ' must be a finite number');
    return value;
  };

  class Point {
    constructor(x, y) {
      if (!finite(x) || !finite(y)) throw new TypeError('a point needs finite x and y');
      this.x = x;
      this.y = y;
    }
    get 0() { return this.x; }
    get 1() { return this.y; }
    *[Symbol.iterator]() { yield this.x; yield this.y; }
    distanceTo(other) {
      const p = point(other, 'distanceTo point');
      return hypot(p.x - this.x, p.y - this.y);
    }
    angleTo(other) {
      const p = point(other, 'angleTo point');
      const degrees = (atan2(p.y - this.y, p.x - this.x) * 180) / PI;
      return degrees < 0 ? degrees + 360 : degrees;
    }
    polar(distance, degrees) {
      number(distance, 'polar distance');
      number(degrees, 'polar angle');
      const radians = (degrees * PI) / 180;
      return new Point(this.x + distance * cos(radians), this.y + distance * sin(radians));
    }
    toString() { return '[' + this.x + ', ' + this.y + ']'; }
    static centroid(...points) {
      if (points.length === 0) throw new TypeError('centroid needs at least one point');
      let x = 0;
      let y = 0;
      for (const each of points) {
        const p = point(each, 'centroid point');
        x += p.x;
        y += p.y;
      }
      return new Point(x / points.length, y / points.length);
    }
  }

  // A Point or an [x, y] array, checked and returned as is; both read as value[0], value[1].
  const checked = (value, what) => {
    if (value instanceof Point) return value;
    if (!isArray(value) || value.length !== 2 || !finite(value[0]) || !finite(value[1])) {
      throw new TypeError(what + ' must be a point [x, y] of finite numbers');
    }
    return value;
  };
  const point = (value, what) => {
    const p = checked(value, what);
    return p instanceof Point ? p : new Point(p[0], p[1]);
  };

  // One crossing to core/geom on the host, as plain numbers, which cross several times faster
  // than JSON. Shapes travel as their record: a kind code, then its numbers. The reply is
  // numbers joined by commas, or "!" and a refusal to throw here, so the error points at
  // the script's line.
  const geometry = (op, numbers) => {
    const reply = host(op, ...numbers);
    if (reply[0] === '!') throw new Error(reply.slice(1));
    return reply === '' ? [] : reply.split(',').map(Number);
  };
  const pointsOf = (numbers) => {
    const points = [];
    for (let i = 0; i + 1 < numbers.length; i += 2) points[points.length] = new Point(numbers[i], numbers[i + 1]);
    return points;
  };
  const ARITY = [4, 3, 5];
  const shapesOf = (numbers) => {
    const made = [];
    for (let i = 0; i < numbers.length; i += 1 + ARITY[numbers[i]]) {
      const [a, b, c, d, e] = numbers.slice(i + 1, i + 1 + ARITY[numbers[i]]);
      made[made.length] =
        numbers[i] === 0 ? new Line(a, b, c, d) : numbers[i] === 1 ? new Circle(a, b, c) : new Arc(a, b, c, d, e);
    }
    return made;
  };

  const shape = (value, what) => {
    if (!(value instanceof Shape)) throw new TypeError(what + ' must be a Line, Circle or Arc');
    return value;
  };

  // A copy holding only the style properties the API knows, so later changes to the
  // script's object cannot reach it.
  const LINES = ${JSON.stringify(LINE_NAMES)};
  const COLOR = /^#[0-9a-fA-F]{6}$/;
  const ARROWS = ${JSON.stringify(ARROW_TYPES)};
  const STYLE_KEYS = ${JSON.stringify(STYLE_KEYS)};
  const oneOf = (list, value) => {
    for (let i = 0; i < list.length; i += 1) if (list[i] === value) return true;
    return false;
  };
  const positive = (value, what) => {
    if (!finite(value) || value <= 0) throw new TypeError('a style ' + what + ' must be a positive number');
    return value;
  };
  const styleOf = (options, base) => {
    if (typeof options !== 'object' || options === null) throw new TypeError('a style must be an object, such as { color: "#cc0000" }');
    const made = {};
    for (let i = 0; base && i < STYLE_KEYS.length; i += 1) {
      if (base[STYLE_KEYS[i]] !== undefined) made[STYLE_KEYS[i]] = base[STYLE_KEYS[i]];
    }
    const { color, line, textHeight, arrowSize, arrow, precision } = options;
    // Deprecated: \`width\` is read as \`thick\` for scripts written before the rename.
    const thick = options.thick !== undefined ? options.thick : options.width;
    if (precision !== undefined) throw new TypeError('a style has no precision; set it for the whole drawing in the Document section');
    if (color !== undefined) {
      if (typeof color !== 'string' || !COLOR.test(color)) throw new TypeError('a style color must be "#rrggbb"');
      made.color = color;
    }
    if (thick !== undefined) made.thick = positive(thick, 'thick');
    if (line !== undefined) {
      if (!oneOf(LINES, line)) throw new TypeError('a style line must be one of ' + LINES.join(', '));
      made.line = line;
    }
    if (textHeight !== undefined) made.textHeight = positive(textHeight, 'textHeight');
    if (arrowSize !== undefined) made.arrowSize = positive(arrowSize, 'arrowSize');
    if (arrow !== undefined) {
      if (!oneOf(ARROWS, arrow)) throw new TypeError('a style arrow must be one of ' + ARROWS.join(', '));
      made.arrow = arrow;
    }
    return made;
  };

  // One per layer in the panel, in its order, so a shape's layer is an index into that list.
  // Where new shapes go: layer 0, or the layer whose draw() block is running.
  let current = 0;
  class Layer {
    constructor(index, name) {
      this._index = index;
      this._name = name;
    }
    get name() { return this._name; }
    draw(block) {
      if (typeof block !== 'function') throw new TypeError('draw needs a function, such as () => { ... }');
      const outer = current;
      current = this._index;
      try {
        return block();
      } finally {
        current = outer;
      }
    }
  }
  const layerList = [];
  for (let i = 0; i < layerNames.length; i += 1) layerList[i] = new Layer(i, layerNames[i]);
  // Pieces and copies of a shape stay on its layer, with its style, inside any draw() block.
  const sameLook = (made, from) => {
    made._layer = from._layer || 0;
    if (from._view !== undefined) {
      made._view = from._view;
      from._view._members[from._view._members.length] = made;
    }
    if (from._style !== undefined) made._style = from._style;
    return made;
  };
  // The view whose draw() block is running, which every shape made in it joins.
  let openView = null;
  // Facts about views cost a pass over every shape, so a drawing without any skips it.
  let anyView = false;
  const drawn = (made) => {
    if (made._layer === undefined && current !== 0) made._layer = current;
    // A piece or copy already joined its shape's view in sameLook.
    if (openView !== null && made._view === undefined) {
      made._view = openView;
      openView._members[openView._members.length] = made;
    }
    if (withLines) made._stack = new Err().stack;
    shapes[shapes.length] = made;
    return made;
  };
  // Every shape is drawn as it stands at the end, so edits after creation are what show.

  // What shapes and dimensions share: a name, a layer, a style, and being taken out.
  class Drawn {
    named(name) {
      if (typeof name !== 'string' || name === '') throw new TypeError('a name must be a non-empty string');
      this._name = name;
      return this;
    }
    remove() {
      this._removed = true;
      return this;
    }
    on(layer) {
      if (!(layer instanceof Layer)) throw new TypeError('a shape goes on a layer, such as layers.holes');
      this._layer = layer._index;
      return this;
    }
    get layer() { return layerList[this._layer || 0]; }
    style(options) {
      this._style = styleOf(options, this._style);
      return this;
    }
    get isGuide() { return this._guide === true; }
    get isRemoved() { return this._removed === true; }
  }

  // Coordinates are kept as plain numbers, and Points are made only when a script reads
  // one, since allocating them for every shape is most of a large script's cost.
  class Shape {
    guide() {
      this._guide = true;
      return this;
    }
    midPoint() { return pointsOf(geometry('midpoint', this.encode()))[0]; }
    quadrants() { return pointsOf(geometry('quadrants', this.encode())); }
    intersections(other) {
      return pointsOf(geometry('intersections', [...this.encode(), ...shape(other, 'intersections with').encode()]));
    }
    nearest(to) {
      const p = checked(to, 'nearest to');
      return pointsOf(geometry('nearest', [...this.encode(), p[0], p[1]]))[0];
    }
    perpendicularFrom(from) {
      const p = checked(from, 'perpendicularFrom point');
      return pointsOf(geometry('perpendicularFrom', [...this.encode(), p[0], p[1]]));
    }
    tangentsFrom(from) {
      const p = checked(from, 'tangentsFrom point');
      return pointsOf(geometry('tangentsFrom', [...this.encode(), p[0], p[1]]));
    }
    offset(distance) {
      return drawn(sameLook(shapesOf(geometry('offset', [...this.encode(), number(distance, 'offset distance')]))[0], this));
    }
    split(...at) {
      const cuts = [];
      for (const each of at) {
        const p = checked(each, 'split point');
        cuts[cuts.length] = p[0];
        cuts[cuts.length] = p[1];
      }
      const pieces = shapesOf(geometry('split', [...this.encode(), ...cuts]));
      this.assign(pieces[0]);
      const made = [];
      for (let i = 1; i < pieces.length; i += 1) made[made.length] = drawn(sameLook(pieces[i], this));
      return made;
    }
    mirror(axis, to) {
      let a;
      let b;
      if (axis instanceof Line && to === undefined) {
        a = [axis._ax, axis._ay];
        b = [axis._bx, axis._by];
      } else {
        a = checked(axis, 'mirror axis start');
        b = checked(to, 'mirror axis end');
      }
      return drawn(sameLook(shapesOf(geometry('mirror', [...this.encode(), a[0], a[1], b[0], b[1]]))[0], this));
    }
    cornerWith(other, options) {
      const met = geometry('corner', [...this.encode(), ...shape(other, 'cornerWith shape').encode(), ...nearOf(options, [], 'cornerWith')]);
      const [a, b] = shapesOf(met.slice(2));
      this.assign(a);
      other.assign(b);
      return new Point(met[0], met[1]);
    }
  }

  // A corner edit's near point as the host reads it: 1 and the point, or 0 for its default.
  const nearOf = (options, more, what) => {
    const { near } = optionsOf(options, ['near', ...more], what);
    if (near === undefined) return [0];
    const p = checked(near, what + ' near');
    return [1, p[0], p[1]];
  };
  // A corner edit's pieces: the two shapes it changed, in place, and what it made between them.
  const cornered = (numbers, a, b) => {
    const [first, second, made] = shapesOf(numbers);
    a.assign(first);
    b.assign(second);
    return drawn(sameLook(made, a));
  };

  class Line {
    constructor(ax, ay, bx, by) {
      this._ax = ax;
      this._ay = ay;
      this._bx = bx;
      this._by = by;
    }
    get start() { return new Point(this._ax, this._ay); }
    get end() { return new Point(this._bx, this._by); }
    length() { return hypot(this._bx - this._ax, this._by - this._ay); }
    _move(dx, dy) {
      this._ax += dx;
      this._ay += dy;
      this._bx += dx;
      this._by += dy;
    }
    encode() { return [0, this._ax, this._ay, this._bx, this._by]; }
    assign(piece) {
      this._ax = piece._ax;
      this._ay = piece._ay;
      this._bx = piece._bx;
      this._by = piece._by;
    }
    record() {
      out[n++] = 0;
      out[n++] = this._ax;
      out[n++] = this._ay;
      out[n++] = this._bx;
      out[n++] = this._by;
    }
    static between(start, end) {
      const a = checked(start, 'line start');
      const b = checked(end, 'line end');
      return drawn(new Line(a[0], a[1], b[0], b[1]));
    }
    static tangentTo(a, b, options) {
      const { near } = optionsOf(options, ['near'], 'Line.tangentTo');
      const p = point(near, 'Line.tangentTo near');
      const touching = [...shape(a, 'tangentTo shape').encode(), ...shape(b, 'tangentTo shape').encode()];
      return drawn(shapesOf(geometry('lineTouching', [...touching, p.x, p.y]))[0]);
    }
    static chamfer(a, b, options) {
      const { distances, distance, angle } = optionsOf(options, ['distances', 'distance', 'angle', 'near'], 'chamfer');
      let size;
      if (distances !== undefined) {
        if (!isArray(distances) || distances.length !== 2) throw new TypeError('chamfer distances must be [first, second]');
        size = [0, number(distances[0], 'chamfer distance'), number(distances[1], 'chamfer distance')];
      } else {
        size = [1, number(distance, 'chamfer distance'), number(angle === undefined ? 45 : angle, 'chamfer angle')];
      }
      const lines = [...line(a, 'chamfer first').encode(), ...line(b, 'chamfer second').encode()];
      const near = nearOf(options, ['distances', 'distance', 'angle'], 'chamfer');
      return cornered(geometry('chamfer', [...lines, ...size, ...near]), a, b);
    }
  }

  class Circle {
    constructor(cx, cy, radius) {
      this._cx = cx;
      this._cy = cy;
      this._radius = radius;
    }
    get center() { return new Point(this._cx, this._cy); }
    get radius() { return this._radius; }
    _move(dx, dy) {
      this._cx += dx;
      this._cy += dy;
    }
    encode() { return [1, this._cx, this._cy, this._radius]; }
    assign() {
      throw new Error('a circle cannot be split; draw the parts as arcs');
    }
    record() {
      out[n++] = 1;
      out[n++] = this._cx;
      out[n++] = this._cy;
      out[n++] = this._radius;
    }
    static at(center, radius) {
      const c = checked(center, 'circle center');
      return drawn(new Circle(c[0], c[1], number(radius, 'circle radius')));
    }
    static tangentTo(touching, options) {
      if (!isArray(touching) || touching.length !== 3) {
        throw new TypeError('tangentTo needs an array of three shapes');
      }
      const near = point(options && options.near, 'tangentTo near');
      const numbers = [];
      for (const each of touching) for (const value of shape(each, 'tangentTo shape').encode()) numbers[numbers.length] = value;
      return drawn(shapesOf(geometry('touchingThree', [...numbers, near.x, near.y]))[0]);
    }
  }

  class Arc {
    constructor(cx, cy, radius, startAngle, endAngle) {
      this._cx = cx;
      this._cy = cy;
      this._radius = radius;
      this._startAngle = startAngle;
      this._endAngle = endAngle;
    }
    get center() { return new Point(this._cx, this._cy); }
    get radius() { return this._radius; }
    get startAngle() { return this._startAngle; }
    get endAngle() { return this._endAngle; }
    get start() { return this.center.polar(this._radius, this._startAngle); }
    get end() { return this.center.polar(this._radius, this._endAngle); }
    _move(dx, dy) {
      this._cx += dx;
      this._cy += dy;
    }
    encode() { return [2, this._cx, this._cy, this._radius, this._startAngle, this._endAngle]; }
    assign(piece) {
      this._cx = piece._cx;
      this._cy = piece._cy;
      this._radius = piece._radius;
      this._startAngle = piece._startAngle;
      this._endAngle = piece._endAngle;
    }
    record() {
      out[n++] = 2;
      out[n++] = this._cx;
      out[n++] = this._cy;
      out[n++] = this._radius;
      out[n++] = this._startAngle;
      out[n++] = this._endAngle;
    }
    static at(center, radius, startAngle, endAngle) {
      const c = checked(center, 'arc center');
      return drawn(
        new Arc(
          c[0],
          c[1],
          number(radius, 'arc radius'),
          number(startAngle, 'arc start angle'),
          number(endAngle, 'arc end angle'),
        ),
      );
    }
    static from(center, start, end) {
      const c = point(center, 'arc center');
      const s = point(start, 'arc start');
      const e = point(end, 'arc end');
      const radius = c.distanceTo(s);
      if (radius === 0) throw new Error('arc start must not be its center');
      return drawn(new Arc(c.x, c.y, radius, c.angleTo(s), c.angleTo(e)));
    }
    static fillet(a, b, radius, options) {
      const shapes = [...shape(a, 'fillet first').encode(), ...shape(b, 'fillet second').encode()];
      const near = nearOf(options, [], 'fillet');
      return cornered(geometry('fillet', [...shapes, number(radius, 'fillet radius'), ...near]), a, b);
    }
  }

  // Options are checked by name, so a misspelled one fails instead of doing nothing.
  const optionsOf = (options, allowed, what) => {
    if (options === undefined) return {};
    if (typeof options !== 'object' || options === null) throw new TypeError(what + ' options must be an object');
    for (const key in options) {
      if (!oneOf(allowed, key)) throw new TypeError(what + ' has no option "' + key + '"; use ' + allowed.join(', '));
    }
    return options;
  };
  const degreesOf = (from, to) => (atan2(to[1] - from[1], to[0] - from[0]) * 180) / PI;
  const DIRECTIONS = ['aligned', 'horizontal', 'vertical'];
  const directionOf = (direction) => {
    if (direction === undefined || direction === 'aligned') return null;
    if (direction === 'horizontal') return 0;
    if (direction === 'vertical') return 90;
    if (finite(direction)) return direction;
    throw new TypeError('a dimension direction must be ' + DIRECTIONS.join(', ') + ', or an angle in degrees');
  };
  const optional = (value, what) => (value === undefined ? null : number(value, what));
  const line = (value, what) => {
    if (!(value instanceof Line)) throw new TypeError(what + ' must be a Line');
    return value;
  };
  const round = (value, what) => {
    if (!(value instanceof Circle) && !(value instanceof Arc)) throw new TypeError(what + ' must be a Circle or Arc');
    return value;
  };
  // An arc's middle, where its dimension goes unless the script says otherwise.
  const middleOf = (shape) => {
    if (!(shape instanceof Arc)) return 45;
    const sweep = (((shape._endAngle - shape._startAngle) % 360) + 360) % 360 || 360;
    return shape._startAngle + sweep / 2;
  };
  const CODES = ${JSON.stringify(DIMENSION_CODES)};
  const ROUND_TEXTS = ${JSON.stringify(ROUND_TEXTS)};
  // A radius or diameter along its angle, its text where the options say: 0 lets it choose.
  const roundDimension = (kind, measured, options) => {
    const { angle, text } = optionsOf(options, ['angle', 'text'], kind);
    const at = optional(angle, 'dimension angle');
    let placed = 0;
    if (text !== undefined) {
      for (let i = 0; i < ROUND_TEXTS.length; i += 1) if (ROUND_TEXTS[i] === text) placed = i + 1;
      if (placed === 0) throw new TypeError("a dimension text must be 'inside' or 'outside'");
    }
    return drawn(
      new Dimension(kind, () => [measured._cx, measured._cy, measured._radius, at === null ? middleOf(measured) : at, placed]),
    );
  };

  // Where a callout's leader starts on what it labels, and its angle, as the shape stands
  // when the script ends: a line's middle, leaving at 45 degrees or, for a line near that
  // slope, 135, so the leader never lies along it; a circle's rim at the angle, 45 by
  // default; an arc's rim there, its middle by default, held to its sweep by its nearer end.
  const calloutStart = (of, angle) => {
    if (of instanceof Line) {
      const slope = ((degreesOf([of._ax, of._ay], [of._bx, of._by]) % 180) + 180) % 180;
      const along = (a) => min(abs(slope - a), 180 - abs(slope - a));
      const at = angle === null ? (along(45) >= along(135) ? 45 : 135) : angle;
      return [(of._ax + of._bx) / 2, (of._ay + of._by) / 2, at];
    }
    const at = angle === null ? middleOf(of) : angle;
    const rim = (degrees) => [
      of._cx + of._radius * cos((degrees * PI) / 180),
      of._cy + of._radius * sin((degrees * PI) / 180),
    ];
    if (of instanceof Circle) return [...rim(at), at];
    const sweep = (((of._endAngle - of._startAngle) % 360) + 360) % 360 || 360;
    const past = (((at - of._startAngle) % 360) + 360) % 360;
    if (past <= sweep) return [...rim(at), at];
    // Outside the sweep: the end the angle is nearer to, measured either way round.
    const toEnd = past - sweep;
    const toStart = 360 - past;
    return [...rim(toEnd <= toStart ? of._endAngle : of._startAngle), at];
  };

  // A dimension measures what it was given as it stands when the script ends, the same
  // moment shapes are drawn, so it follows edits such as split.
  class Dimension {
    constructor(kind, read) {
      this._kind = kind;
      this._read = read;
    }
    // A dimension given shapes follows them; one given points moves with its view.
    _move(dx, dy) {
      if (this._points) {
        this._points[0] += dx;
        this._points[1] += dy;
        this._points[2] += dx;
        this._points[3] += dy;
      }
    }
    record() {
      const numbers = this._read();
      out[n++] = CODES[this._kind];
      for (let i = 0; i < numbers.length; i += 1) out[n++] = numbers[i];
    }
    static between(from, to, options) {
      const a = checked(from, 'dimension from');
      const b = checked(to, 'dimension to');
      const { direction, offset } = optionsOf(options, ['direction', 'offset'], 'between');
      const angle = directionOf(direction);
      const placed = optional(offset, 'dimension offset');
      const numbers = [a[0], a[1], b[0], b[1], angle === null ? degreesOf(a, b) : angle, placed || 0, placed === null ? 0 : 1];
      const made = new Dimension('linear', () => numbers);
      made._points = numbers;
      return drawn(made);
    }
    static length(of, options) {
      const measured = line(of, 'length of');
      const { direction, offset } = optionsOf(options, ['direction', 'offset'], 'length');
      const angle = directionOf(direction);
      const placed = optional(offset, 'dimension offset');
      return drawn(
        new Dimension('linear', () => [
          measured._ax, measured._ay, measured._bx, measured._by,
          angle === null ? degreesOf([measured._ax, measured._ay], [measured._bx, measured._by]) : angle,
          placed || 0, placed === null ? 0 : 1,
        ]),
      );
    }
    static radius(of, options) {
      return roundDimension('radius', round(of, 'radius of'), options);
    }
    static diameter(of, options) {
      return roundDimension('diameter', round(of, 'diameter of'), options);
    }
    static callout(of, text, options) {
      if (!(of instanceof Line) && !(of instanceof Circle) && !(of instanceof Arc))
        throw new TypeError('callout of must be a Line, Circle or Arc');
      if (typeof text !== 'string' || text.trim() === '')
        throw new TypeError("a callout needs text, such as 'Drill through'");
      if (text.indexOf('\\n') >= 0) throw new TypeError("a callout's text is one line");
      const { angle, length } = optionsOf(options, ['angle', 'length'], 'callout');
      const at = optional(angle, 'callout angle');
      const long = optional(length, 'callout length');
      if (long !== null && long <= 0) throw new TypeError('a callout length must be positive');
      const made = new Dimension('callout', () => [
        ...calloutStart(of, at),
        long || 0,
        long === null ? 0 : 1,
        text.length,
      ]);
      made._label = text;
      return drawn(made);
    }
    static angle(first, second, options) {
      const a = line(first, 'angle first');
      const b = line(second, 'angle second');
      const turn = (a._bx - a._ax) * (b._by - b._ay) - (a._by - a._ay) * (b._bx - b._ax);
      if (turn === 0) throw new Error('an angle needs two lines that are not parallel');
      const { radius } = optionsOf(options, ['radius'], 'angle');
      const placed = optional(radius, 'dimension radius');
      if (placed !== null && placed <= 0) throw new TypeError('a dimension radius must be positive');
      return drawn(
        new Dimension('angle', () => [
          a._ax, a._ay, a._bx, a._by, b._ax, b._ay, b._bx, b._by, placed || 0, placed === null ? 0 : 1,
        ]),
      );
    }
  }

  // The sizes the host needs to lay out a dimension or a title: the object's own style, then
  // its layer's, and -1 for the default.
  const lookNumbers = (made) => {
    const own = made._style || {};
    const layer = layerLooks[made._layer || 0] || [];
    let arrow = -1;
    for (let i = 0; i < ARROWS.length; i += 1) if (ARROWS[i] === own.arrow) arrow = i;
    return [
      own.textHeight !== undefined ? own.textHeight : layer[0] === undefined ? -1 : layer[0],
      own.arrowSize !== undefined ? own.arrowSize : layer[1] === undefined ? -1 : layer[1],
      arrow >= 0 ? arrow : layer[2] === undefined ? -1 : layer[2],
    ];
  };

  // A named frame around everything its draw() block makes, sized as it stands when asked,
  // and drawn as it stands when the script ends.
  class View {
    constructor(margin) {
      this._margin = margin;
      this._members = [];
    }
    bounds() {
      const numbers = [this._margin, ...lookNumbers(this), this._name.length];
      for (let i = 0; i < this._members.length; i += 1) {
        const each = this._members[i];
        if (each.isGuide || each.isRemoved) continue;
        if (each instanceof Dimension) {
          numbers[numbers.length] = CODES[each._kind];
          const read = each._read();
          for (let j = 0; j < read.length; j += 1) numbers[numbers.length] = read[j];
          const look = lookNumbers(each);
          for (let j = 0; j < look.length; j += 1) numbers[numbers.length] = look[j];
        } else {
          const encoded = each.encode();
          for (let j = 0; j < encoded.length; j += 1) numbers[numbers.length] = encoded[j];
        }
      }
      const [minX, minY, maxX, maxY] = geometry('viewFrame', numbers);
      return { min: new Point(minX, minY), max: new Point(maxX, maxY) };
    }
    record() {
      out[n++] = ${VIEW_CODE};
      out[n++] = this._margin;
    }
    static draw(name, block, options) {
      if (typeof name !== 'string' || name === '') throw new TypeError('a view needs a name, such as "Front"');
      if (typeof block !== 'function') throw new TypeError('a view needs a function, such as () => { ... }');
      if (openView !== null) throw new Error('a view cannot be drawn inside another view');
      const { at } = optionsOf(options, ['at'], 'View.draw');
      const origin = at === undefined ? [0, 0] : checked(at, 'view at');
      const view = new View(viewMargin);
      view._name = name;
      anyView = true;
      openView = view;
      try {
        block();
      } finally {
        openView = null;
      }
      // The block draws around the origin; the whole view then moves to where it goes.
      if (origin[0] !== 0 || origin[1] !== 0) {
        for (let i = 0; i < view._members.length; i += 1) view._members[i]._move(origin[0], origin[1]);
      }
      return drawn(view);
    }
  }

  // Inherited without super() calls, which cost noticeably per shape in the interpreter.
  for (const kind of [Line, Circle, Arc]) Object.setPrototypeOf(kind.prototype, Shape.prototype);
  Object.setPrototypeOf(Shape.prototype, Drawn.prototype);
  Object.setPrototypeOf(Dimension.prototype, Drawn.prototype);
  Object.setPrototypeOf(View.prototype, Drawn.prototype);

  // Read-only, and a name the panel does not have throws on the script's line rather than
  // reading as undefined. A null prototype lets "in" answer without any built-in.
  const known = Object.create(null);
  for (const key of Object.keys(values)) known[key] = values[key];
  const readOnly = () => {
    throw new TypeError('params are read-only; change them in the Parameters panel');
  };
  const params = new Proxy(known, {
    get(target, key) {
      if (typeof key !== 'string') return undefined;
      if (key in target) return target[key];
      if (key === 'toJSON') return undefined;
      throw new ReferenceError('no param "' + key + '"; add it in the Parameters panel');
    },
    set: readOnly,
    defineProperty: readOnly,
    deleteProperty: readOnly,
    setPrototypeOf: readOnly,
  });

  // The same for layers: read-only, and a name the panel does not have throws.
  const knownLayers = Object.create(null);
  for (let i = 0; i < layerList.length; i += 1) knownLayers[layerList[i]._name] = layerList[i];
  const layersReadOnly = () => {
    throw new TypeError('layers are read-only; change them in the Layers panel');
  };
  const layers = new Proxy(knownLayers, {
    get(target, key) {
      if (typeof key !== 'string') return undefined;
      if (key in target) return target[key];
      if (key === 'toJSON') return undefined;
      throw new ReferenceError('no layer "' + key + '"; add it in the Layers panel');
    },
    set: layersReadOnly,
    defineProperty: layersReadOnly,
    deleteProperty: layersReadOnly,
    setPrototypeOf: layersReadOnly,
  });

  for (const [name, value] of Object.entries({ Point, Line, Circle, Arc, Dimension, View, params, layers })) {
    Object.defineProperty(globalThis, name, { value, writable: false, configurable: false });
  }
  Object.defineProperty(globalThis, '__drawRecords', {
    value: () => {
      for (let i = 0; i < shapes.length; i += 1) {
        if (!shapes[i].isGuide && !shapes[i].isRemoved) shapes[i].record();
      }
      return new Float64(out).buffer;
    },
    writable: false,
    configurable: false,
  });
  // What the records do not carry, in one crossing: each drawn shape's .named() name or
  // null; a record index and layer index for each shape not on layer 0; the style of each
  // shape that sets one; the text of each that carries one, such as a callout; a record
  // index and its view's for each shape in a drawn view; and, when asked, the stack that
  // made each shape, read by the host.
  Object.defineProperty(globalThis, '__drawFacts', {
    value: () => {
      const names = [];
      const stacks = [];
      const layered = [];
      const styled = [];
      const labeled = [];
      const viewed = [];
      // Each recorded object's index, read below for the views that come after their shapes.
      let at = 0;
      for (let i = 0; anyView && i < shapes.length; i += 1) {
        if (!shapes[i].isGuide && !shapes[i].isRemoved) shapes[i]._at = at++;
      }
      for (let i = 0; i < shapes.length; i += 1) {
        const each = shapes[i];
        if (each.isGuide || each.isRemoved) continue;
        if (anyView && each._view !== undefined && !each._view.isRemoved) {
          viewed[viewed.length] = names.length;
          viewed[viewed.length] = each._view._at;
        }
        if (each._layer) {
          layered[layered.length] = names.length;
          layered[layered.length] = each._layer;
        }
        if (each._style) styled[styled.length] = { at: names.length, style: each._style };
        if (each._label !== undefined) labeled[labeled.length] = { at: names.length, text: each._label };
        names[names.length] = each._name || null;
        if (withLines) stacks[stacks.length] = each._stack;
      }
      return stringify(
        withLines ? { names, layered, styled, labeled, viewed, stacks } : { names, layered, styled, labeled, viewed },
      );
    },
    writable: false,
    configurable: false,
  });
})();
`;

/**
 * How to read the recorded numbers back: each drawn shape writes its index in this list,
 * then `arity` numbers, and `decode` turns those numbers into the shape.
 */
export const CALLS: readonly { readonly arity: number; decode(n: readonly number[]): Shape }[] = [
  { arity: 4, decode: ([ax, ay, bx, by]) => segment(vec(ax!, ay!), vec(bx!, by!)) },
  { arity: 3, decode: ([x, y, r]) => circle(vec(x!, y!), r!) },
  { arity: 5, decode: ([x, y, r, start, end]) => arc(vec(x!, y!), r!, start!, end!) },
];

/** Type declarations for the API, for the code editor's completion and checking. */
export const API_TYPES = `
/** A point as written in a script: a Point, or [x, y]. */
type PointLike = Point | readonly [x: number, y: number];

/**
 * A point in world units. Destructures like an array: \`const [x, y] = p\`.
 */
declare class Point {
  constructor(x: number, y: number);
  readonly x: number;
  readonly y: number;
  readonly 0: number;
  readonly 1: number;
  [Symbol.iterator](): Iterator<number>;
  /** The distance to \`other\`. */
  distanceTo(other: PointLike): number;
  /** The angle from this point to \`other\`, in degrees counter-clockwise from +x. */
  angleTo(other: PointLike): number;
  /**
   * The point \`distance\` away at \`degrees\` counter-clockwise from +x.
   * @example const tip = base.polar(100, 30);
   */
  polar(distance: number, degrees: number): Point;
  /** The average of the points. */
  static centroid(...points: PointLike[]): Point;
}

/**
 * What every shape can answer. Questions never change the shape; edits change it in place.
 * Every shape is drawn as it stands when the script ends, unless it is a guide.
 */
declare abstract class Shape {
  /**
   * Names the shape in the Objects list and the Properties card; without it, a shape shows
   * its kind and number, such as \`line-3\`.
   * @example Circle.at(center, 3).named('hole');
   */
  named(name: string): this;
  /**
   * Puts this shape on \`layer\`. A new shape goes on layer \`0\`, or on the layer whose
   * \`draw()\` block made it; its pieces and copies stay on its layer.
   * @example Circle.at(center, 3).on(layers.holes);
   */
  on(layer: Layer): this;
  /** The layer this shape is on. */
  readonly layer: Layer;
  /**
   * Overrides some of its layer's style for this shape alone; whatever it leaves out still
   * comes from the layer. Later calls add to earlier ones.
   * @example Line.between(a, b).style({ line: 'dashed' });
   */
  style(style: Style): this;
  /**
   * Marks this shape as construction geometry: usable in questions, never drawn.
   * @example const ring = Circle.at(center, 20).guide();
   */
  guide(): this;
  readonly isGuide: boolean;
  /**
   * Takes this shape out of the drawing. It can still answer questions.
   * @example const [notch] = top.split(left, right); notch.remove();
   */
  remove(): this;
  readonly isRemoved: boolean;
  /** The middle of a line or an arc. A circle has none. */
  midPoint(): Point;
  /** The 0, 90, 180 and 270 degree points that lie on a circle or arc. */
  quadrants(): Point[];
  /**
   * Where this shape crosses \`other\`, ordered along this shape; empty when they miss.
   * @example const [left, right] = top.intersections(ring);
   */
  intersections(other: Shape): Point[];
  /** The point on this shape closest to \`to\`. */
  nearest(to: PointLike): Point;
  /** The feet of the perpendiculars from \`from\` onto this shape. */
  perpendicularFrom(from: PointLike): Point[];
  /** Where lines through \`from\` touch this shape; empty for a line. */
  tangentsFrom(from: PointLike): Point[];
  /**
   * A new copy \`distance\` away: to the left of a line's direction, or outward from a
   * circle's or arc's center when positive.
   */
  offset(distance: number): this;
  /**
   * Cuts this shape at the points, in place: it keeps the first piece along its direction
   * and returns the others in order, all still drawn.
   * @example const [middle, rest] = top.split(a, b); middle.remove();
   */
  split(...at: PointLike[]): (Line | Arc)[];
  /**
   * A new copy reflected across \`axis\`, or across the line through two points; a reflected
   * arc still runs counter-clockwise, so its ends swap.
   * @example const right = halves.map((half) => half.mirror(centerLine));
   */
  mirror(axis: Line): this;
  mirror(from: PointLike, to: PointLike): this;
  /**
   * Trims or extends this shape and \`other\`, in place, to end where they cross, each
   * keeping the side \`near\` is on; returns the corner. Without \`near\`, the middle of
   * the two shapes picks, which suits two lines meeting like an L. A circle cannot be
   * trimmed: draw the part to keep as an arc.
   * @example const tip = left.cornerWith(right);
   */
  cornerWith(other: Line | Arc, options?: { near?: PointLike }): Point;
}

/** Picks the corner a fillet or chamfer goes in; the middle of the two shapes without it. */
interface CornerOptions {
  near?: PointLike;
}

/** A straight line from \`start\` to \`end\`. */
declare class Line extends Shape {
  readonly start: Point;
  readonly end: Point;
  length(): number;
  /** @example const bottom = Line.between([0, 0], [width, 0]); */
  static between(start: PointLike, end: PointLike): Line;
  /**
   * The line touching two circles or arcs, from its touch point on \`a\` to the one on
   * \`b\`: of the outer and inner ones, the nearest \`near\`.
   * @example const belt = Line.tangentTo(pulley, idler, { near: [0, 50] });
   */
  static tangentTo(a: Circle | Arc, b: Circle | Arc, options: { near: PointLike }): Line;
  /**
   * The line cutting the corner between two lines, with both trimmed to it in place:
   * \`distances\` along \`a\` and \`b\` from the corner, or \`distance\` along \`a\` and the
   * cut's \`angle\` to \`a\` in degrees, 45 if left out. It goes on \`a\`'s layer.
   * @example Line.chamfer(bottom, side, { distances: [3, 5] });
   */
  static chamfer(
    a: Line,
    b: Line,
    options: CornerOptions & ({ distances: [number, number] } | { distance: number; angle?: number }),
  ): Line;
}

declare class Circle extends Shape {
  readonly center: Point;
  readonly radius: number;
  /** @example Circle.at(corner.center, 3); */
  static at(center: PointLike, radius: number): Circle;
  /**
   * The circle touching three shapes, each on its side facing \`near\`.
   * @example Circle.tangentTo([a, b, c], { near: Point.centroid(a.center, b.center, c.center) });
   */
  static tangentTo(shapes: [Shape, Shape, Shape], options: { near: PointLike }): Circle;
}

/** An arc swept counter-clockwise from its start angle to its end angle. */
declare class Arc extends Shape {
  readonly center: Point;
  readonly radius: number;
  /** Degrees counter-clockwise from +x. */
  readonly startAngle: number;
  readonly endAngle: number;
  /** The point at the start angle. */
  readonly start: Point;
  /** The point at the end angle. */
  readonly end: Point;
  /** @example Arc.at([0, 0], 10, 0, 90); */
  static at(center: PointLike, radius: number, startAngle: number, endAngle: number): Arc;
  /**
   * The arc around \`center\` from \`start\` to \`end\`, counter-clockwise; its radius comes from
   * \`start\`. Swap the two points to take the other side of the circle.
   * @example Arc.from(top.midPoint(), left, right);
   */
  static from(center: PointLike, start: PointLike, end: PointLike): Arc;
  /**
   * The arc of \`radius\` rounding the corner between two lines or arcs, with both trimmed or
   * extended to its ends in place. It goes on \`a\`'s layer.
   * @example Arc.fillet(bottom, side, 4);
   */
  static fillet(a: Line | Arc, b: Line | Arc, radius: number, options?: CornerOptions): Arc;
}

/**
 * A measurement drawn on the drawing: extension lines, a dimension line with arrows, and its
 * value. It measures what it was given as it stands when the script ends. Its text size,
 * arrows and decimal places come from its layer's style; nothing snaps to it.
 */
/** Where a radius or diameter goes: along \`angle\` in degrees, with its text \`inside\` or \`outside\` the circle. */
interface RoundOptions {
  angle?: number;
  text?: 'inside' | 'outside';
}

/** Where a callout's leader goes: out along \`angle\` in degrees, \`length\` before its text. */
interface CalloutOptions {
  angle?: number;
  length?: number;
}

declare class Dimension {
  /** Names it in the Objects list. */
  named(name: string): this;
  /** Puts it on \`layer\`, whose style sizes its text and arrows. */
  on(layer: Layer): this;
  readonly layer: Layer;
  /** Overrides some of its layer's style for this dimension alone. */
  style(style: Style): this;
  /** Takes it out of the drawing. */
  remove(): this;
  readonly isRemoved: boolean;
  /**
   * The distance between two points, drawn \`offset\` to the left of the way from \`from\` to
   * \`to\`; a negative offset goes right. Without one it sits clear of its text.
   * @example Dimension.between(bottom.start, bottom.end, { offset: -10 });
   */
  static between(from: PointLike, to: PointLike, options?: LinearOptions): Dimension;
  /**
   * A line's length, measured as the line stands when the script ends.
   * @example Dimension.length(top, { direction: 'horizontal', offset: 10 });
   */
  static length(line: Line, options?: LinearOptions): Dimension;
  /**
   * A circle's or arc's radius, along \`angle\`: 45 degrees on a circle, an arc's middle.
   * Its text goes inside when it fits and outside on a leader when not, unless \`text\` says.
   * @example Dimension.radius(notch);
   */
  static radius(shape: Circle | Arc, options?: RoundOptions): Dimension;
  /** A circle's or arc's diameter, across it along \`angle\`, its text placed as a radius's is. */
  static diameter(shape: Circle | Arc, options?: RoundOptions): Dimension;
  /**
   * The angle between two lines, of at most 180 degrees, between the ends farther from where
   * they meet; \`radius\` sets the arc's distance from there.
   * @example Dimension.angle(bottom, slope, { radius: 20 });
   */
  static angle(first: Line, second: Line, options?: { radius?: number }): Dimension;
  /**
   * A label on a shape: a plain leader from it out along \`angle\`, \`length\` long (two text
   * heights unless given), with the text riding the leader past its end. The leader starts at
   * a line's middle, leaving at 45 degrees, or 135 for a line near that slope; on a circle's rim
   * at the angle, 45 by default; on an arc's rim, its middle by default. The text is one line,
   * sized by the layer as a dimension's is.
   * @example Dimension.callout(hole, 'Drill through');
   * @example Dimension.callout(bottom, 'Weld both sides', { angle: 135, length: 12 });
   */
  static callout(shape: Line | Circle | Arc, text: string, options?: CalloutOptions): Dimension;
}

/**
 * A named frame around everything its \`draw()\` block makes, titled with its name, a margin
 * clear of it; the margin is set in the Document section. Its frame and title use its
 * layer's style; nothing snaps to it. Place each view with \`at\`, clear of the others'
 * bounds.
 * @example
 * const front = View.draw('Front', () => { Line.between([0, 0], [40, 0]); });
 * View.draw('Side', () => { Line.between([0, 0], [20, 0]); }, { at: [front.bounds().max.x + 30, 0] });
 */
declare class View {
  /** Renames it, which retitles it. */
  named(name: string): this;
  /** Puts its frame and title on \`layer\`; what it holds keeps its own layers. */
  on(layer: Layer): this;
  readonly layer: Layer;
  /** Overrides some of its layer's style for its frame and title. */
  style(style: Style): this;
  /** Takes the frame and title out; what it holds stays drawn. */
  remove(): this;
  readonly isRemoved: boolean;
  /**
   * The frame's corners, title included, around what it holds as it stands now. Asked after
   * \`View.draw\` returns, it includes the move to \`at\`, so the next view can be placed
   * clear of it.
   */
  bounds(): { min: Point; max: Point };
  /**
   * Runs \`block\`, putting every shape and dimension it makes in a view named \`name\`; pieces
   * and copies of them made later join it too. Views do not nest.
   *
   * Draw the block around the origin and give \`at\`: when the block ends, everything in it
   * moves by \`at\`, so each view keeps its own coordinates. A dimension given shapes
   * follows them; one given points moves with the view.
   * @example View.draw('Side', () => { ... }, { at: [front.bounds().max.x + 30, 0] });
   */
  static draw(name: string, block: () => void, options?: { at?: PointLike }): View;
}

interface LinearOptions {
  /**
   * What is measured: the straight distance (\`'aligned'\`, the default), only its x or y
   * part, or its part along an angle in degrees.
   */
  direction?: 'aligned' | 'horizontal' | 'vertical' | number;
  /** How far the dimension line sits to the left of the measured direction. */
  offset?: number;
}

/**
 * A named set of shapes that share a style, kept as a layer in DXF export. Layers are made,
 * styled, hidden and locked in the Layers panel; a script reads them from \`layers\`. Layer
 * \`0\` always exists and holds every shape not put on another.
 */
interface Layer {
  readonly name: string;
  /**
   * Runs \`block\`, putting every shape it makes on this layer; afterwards new shapes go
   * where they went before. Blocks nest, and \`.on()\` still moves a single shape.
   * @example layers.holes.draw(() => { for (const c of corners) Circle.at(c, 3); });
   */
  draw<T>(block: () => T): T;
}

/**
 * How a shape or dimension looks.
 * Text height and arrows size dimensions, and are usually set on their layer.
 */
interface Style {
  /** \`'#rrggbb'\`. */
  color?: string;
  /** Stroke thickness in drawing units; the canvas never draws it thinner than a pixel. */
  thick?: number;
  line?: ${LINE_NAMES.map((name) => `'${name}'`).join(' | ')};
  /** A dimension's text height, in drawing units. */
  textHeight?: number;
  /** A dimension's arrowhead length, in drawing units. */
  arrowSize?: number;
  arrow?: ${ARROW_TYPES.map((name) => `'${name}'`).join(' | ')};
}
`;
